import canopyUrls from 'canopy-urls!sofe'
import { of, throwError } from 'rxjs'
import { tap, pluck, catchError } from 'rxjs/operators'
import { isEmpty } from 'lodash'
import { fetchAsObservable } from 'fetcher!sofe'
import { featureEnabled } from 'feature-toggles!sofe'

const headers =
  typeof Headers !== 'undefined'
    ? new Headers({
        'Content-Type': 'application/json',
      })
    : {}

export const maxPageSize = 100

export function getTasks(params) {
  const {
    pageNum = 0,
    filterAndSortObj = {},
    visibleColumns = [],
    includeCompleted = false,
    snapshotId,
    contactId,
    pageSize = maxPageSize,
    type,
    includeTime = true,
    showSubtasks = false,
  } = params

  let payload = {
    method: !snapshotId ? 'POST' : 'GET',
    headers,
  }

  let body

  if (featureEnabled('date_columns')) {
    body = {
      filters: filterAndSortObj,
      visible_columns: visibleColumns,
    }
  } else {
    body = filterAndSortObj
  }

  if (!snapshotId) {
    payload.body = JSON.stringify(body)
  }

  const base = `${canopyUrls.getWorkflowUrl()}/api/task_dashboard`
  const pageNumAndLimit = `page=${pageNum}&limit=${pageSize}`
  const snapshotIdAndCompleted = `${snapshotId ? `&snapshot=${snapshotId}` : ''}${
    includeCompleted ? '&include=completed' : ''
  }`
  const contactIdUrl = `${contactId ? `&client_id=${contactId}` : ''}`
  const filterByType = `${type ? `&type=${type}` : ''}`
  const include_time = `${includeTime ? `&include_time=true` : ''}`
  const include_subtasks = `&include_subtasks=${showSubtasks}`
  const include_pin = `${contactId ? '&include_pin=false' : ''}`

  const queryParams = `?${pageNumAndLimit}${snapshotIdAndCompleted}${contactIdUrl}${filterByType}${include_time}${include_subtasks}${include_pin}`

  return fetchAsObservable(`${base}${queryParams}`, payload)
}

export function getArchivedTasks(name_filter) {
  return fetchAsObservable(
    `${canopyUrls.getWorkflowUrl()}/api/tasks?archived=1${!isEmpty(name_filter) ? `&name_filter=${name_filter}` : ''}`,
    {
      method: 'GET',
      headers,
    }
  )
}

export function postTask(task) {
  return fetchAsObservable(`${canopyUrls.getWorkflowUrl()}/api/tasks`, {
    method: 'POST',
    headers,
    body: `{"tasks": ${JSON.stringify(task)}}`,
  }).pipe(
    tap((resp) => {
      window.dispatchEvent(new CustomEvent('cp:task-created', { detail: (resp && resp.tasks) || task }))
    })
  )
}

export function patchTask(ids, task, toInclude = []) {
  if (typeof ids === 'string') {
    ids = ids.split(',')
  }
  return fetchAsObservable(`${canopyUrls.getWorkflowUrl()}/api/tasks/?include=${toInclude.join(',')}`, {
    method: 'PATCH',
    headers: headers,
    body: `{"task_ids": ${JSON.stringify(ids)}, "tasks": ${JSON.stringify(task)}}`,
  })
}

export function patchTaskDate(dateId, body) {
  return fetchAsObservable(`${canopyUrls.getWorkflowUrl()}/api/task-dates/${dateId}`, {
    method: 'PATCH',
    headers: headers,
    body,
  })
}

export function getTask(id, includes = []) {
  return fetchAsObservable(`${canopyUrls.getWorkflowUrl()}/api/tasks/${id}?include=files,${includes.join(',')}`, {
    method: 'GET',
    headers,
  }).pipe(
    pluck('tasks'),
    catchError((err) => {
      if (
        [403, 404].includes(err.status) ||
        err
          .toString()
          .toLowerCase()
          .indexOf('failed to fetch') !== -1
      ) {
        window.dispatchEvent(new CustomEvent('taskunavailable'))
        return of([])
      } else {
        return throwError()
      }
    })
  )
}

export function getSubtasksOfTask(id) {
  return getTask(id).pipe(pluck('subtasks'))
}

export function deleteTask(id) {
  return fetchAsObservable(`${canopyUrls.getWorkflowUrl()}/api/tasks/${id}`, {
    method: 'DELETE',
    headers: headers,
  })
}

export function archiveTasks(ids, force) {
  return newTasksBulkAction(ids, 'archive', force)
}

export function unarchiveTasks(ids) {
  return newTasksBulkAction(ids, 'unarchive')
}

export function bulkAssign(taskIds, userIds) {
  return tasksBulkAction(taskIds, 'assign', { assigned_to: userIds })
}

export function tasksBulkAction(ids, action, body = {}) {
  return fetchAsObservable(`${canopyUrls.getWorkflowUrl()}/api/tasks/${ids}:${action}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  })
}

export function newTasksBulkAction(ids, action, force) {
  return fetchAsObservable(`${canopyUrls.getWorkflowUrl()}/api/tasks:${action}`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ force, task_ids: ids }),
  })
}

// Take out the prop dateId with the feature toggle date_columns
export function setPinStatus(taskID, dateID = null, pinned) {
  let body = featureEnabled('date_columns')
    ? `{
    "pinned": ${pinned}
  }`
    : `{
    "pinned": ${pinned},
    "task_date_id": ${dateID ? `"${dateID}"` : null}
  }`
  return fetchAsObservable(`${canopyUrls.getWorkflowUrl()}/api/tasks/${taskID}/pin`, {
    method: 'PATCH',
    headers: headers,
    body: body,
  })
}

export function getColumnFilterOptions(columnKey, includeCompletedStatus = false, snapshot_id = null) {
  return fetchAsObservable(
    `${canopyUrls.getWorkflowUrl()}/api/task_dashboard/get_filter_options?filter_type=${columnKey}${
      includeCompletedStatus ? '&include=completed' : ''
    }${snapshot_id ? '&snapshot_id=' + snapshot_id : ''}`,
    {
      method: 'GET',
      headers: headers,
    }
  ).pipe(pluck('options'))
}

export function deleteRelationship(recordType, recordId, relationships) {
  // api/relationships/{record_type}/{record_id}
  return fetchAsObservable(`${canopyUrls.getWorkflowUrl()}/api/relationships/${recordType}/${recordId}`, {
    method: 'DELETE',
    headers: headers,
    body: JSON.stringify({ relationships }),
  })
}

export function completeTask(taskId, complete) {
  return fetchAsObservable(`${canopyUrls.getWorkflowUrl()}/api/tasks/${taskId}/complete`, {
    method: 'PATCH',
    headers: headers,
    body: JSON.stringify({ complete }),
  })
}

export function getTaskDashboardFilterGroups() {
  return fetchAsObservable(`${canopyUrls.getWorkflowUrl()}/api/filtergroups/?type=tasks`, {
    headers,
  })
}

export function addFilterGroup(payload) {
  return fetchAsObservable(`${canopyUrls.getWorkflowUrl()}/api/filtergroups`, {
    headers,
    body: JSON.stringify(payload),
    method: 'POST',
  })
}

export function patchFilterGroup(id, preferences, name) {
  let filterGroupsObj = {
    filter_groups: {
      data: { jql: preferences },
    },
  }
  if (name) filterGroupsObj.filter_groups.name = name.trim()
  return fetchAsObservable(`${canopyUrls.getWorkflowUrl()}/api/filtergroups/${id}`, {
    headers,
    body: JSON.stringify(filterGroupsObj),
    method: 'PATCH',
  })
}

export function patchFilterGroups(filterGroups) {
  return fetchAsObservable(`${canopyUrls.getWorkflowUrl()}/api/filtergroups`, {
    headers,
    body: JSON.stringify({ filter_groups: filterGroups }),
    method: 'PUT',
  }).pipe(pluck('filter_groups'))
}

export function deleteFilterGroup(id) {
  return fetchAsObservable(`${canopyUrls.getWorkflowUrl()}/api/filtergroups/${id}`, {
    headers,
    method: 'DELETE',
  })
}

export function undoDeleteFilterGroup(id) {
  return fetchAsObservable(`${canopyUrls.getWorkflowUrl()}/api/filtergroups/${id}`, {
    headers,
    body: JSON.stringify({ filter_groups: { deleted: false } }),
    method: 'PATCH',
  })
}

export function getExportCSV(showSubtasks, filters, visible_columns, completed) {
  let includeCompleted = completed ? '&include=completed' : ''
  return fetchAsObservable(
    `${canopyUrls.getWorkflowUrl()}/api/task_dashboard?export=true&include_subtasks=${showSubtasks}${includeCompleted}`,
    {
      method: 'POST',
      body: JSON.stringify({
        filters,
        visible_columns,
      }),
    }
  )
}
